import { AD_RELATIONSHIP_LABELS } from './ad-relationships';
import type { RelationshipDefinition } from './types';

/**
 * All AD entity types (primary + secondary labels)
 * Used to define valid AD relationship combinations
 */
const AD_ENTITY_TYPES = [
  'ADObject', // Primary label
  'ADUser',
  'ADComputer',
  'ADGroup',
  'ADGPO',
  'ADOU',
  'ADContainer',
  'ADDomain',
  'ADLocalGroup',
  'ADLocalUser',
  'ADAIACA',
  'ADRootCA',
  'ADEnterpriseCA',
  'ADNTAuthStore',
  'ADCertTemplate',
  'ADIssuancePolicy',
] as const;

/**
 * DISCOVERED relationship - bidirectional between Assets and ADObjects
 * Valid combinations:
 * - Asset ↔ Asset
 * - Repository ↔ Repository
 * - Asset ↔ Any AD entity type
 */
export const DISCOVERED_RELATIONSHIP: RelationshipDefinition = {
  labels: [{ actual: 'DISCOVERED', display: 'Discovered' }],
  bidirectional: true,
  allowedBetween: [
    { source: 'Asset', target: 'Asset' },
    { source: 'Repository', target: 'Repository' },
    // Asset can be discovered with any AD entity type
    ...AD_ENTITY_TYPES.map(adType => ({ source: 'Asset', target: adType })),
  ],
};

/**
 * HAS_PORT relationship - directional from Asset to Port only
 * Valid combinations:
 * - Asset → Port (NOT Port → Asset)
 */
export const HAS_PORT_RELATIONSHIP: RelationshipDefinition = {
  labels: [{ actual: 'HAS_PORT', display: 'Has Port' }],
  bidirectional: false,
  allowedBetween: [{ source: 'Asset', target: 'Port' }],
};

/**
 * HAS_VULNERABILITY relationship - directional from Asset to Risk
 * Valid combinations:
 * - Asset → Risk (NOT Risk → Asset)
 */
export const HAS_VULNERABILITY_RELATIONSHIP: RelationshipDefinition = {
  labels: [{ actual: 'HAS_VULNERABILITY', display: 'Has Vulnerability' }],
  bidirectional: false,
  allowedBetween: [
    { source: 'Asset', target: 'Risk' },
    { source: 'Webpage', target: 'Risk' },
    { source: 'Repository', target: 'Risk' },
  ],
};

/**
 * Generate all valid AD entity combinations for relationships
 * Any AD entity can have a relationship with any other AD entity
 */
const AD_ALLOWED_BETWEEN = AD_ENTITY_TYPES.flatMap(source =>
  AD_ENTITY_TYPES.map(target => ({ source, target }))
);

/**
 * All AD relationships - 85 labels sharing the same rules
 * Valid combinations:
 * - Any AD entity ↔ Any AD entity (all directions)
 *   Examples: ADObject ↔ ADUser, ADComputer ↔ ADGroup, ADUser ↔ ADUser, etc.
 *
 * Uses aliasing pattern: one definition, 85 labels, 256 allowed combinations (16x16)
 */
export const AD_RELATIONSHIPS: RelationshipDefinition = {
  labels: AD_RELATIONSHIP_LABELS,
  bidirectional: true,
  allowedBetween: AD_ALLOWED_BETWEEN,
};

/**
 * Complete list of all relationship definitions
 * Single source of truth for relationship rules
 */
export const ALL_RELATIONSHIP_DEFINITIONS: RelationshipDefinition[] = [
  DISCOVERED_RELATIONSHIP,
  HAS_PORT_RELATIONSHIP,
  HAS_VULNERABILITY_RELATIONSHIP,
  AD_RELATIONSHIPS,
];
